import { createStore, select, withProps } from '@ngneat/elf';
import {
  persistState,
  sessionStorageStrategy,
} from '@ngneat/elf-persist-state';
import { useObservable } from '@ngneat/react-rxjs';

import {
  CompanyLocationEnum,
  Focus,
  OrderEnum,
  SeniorityEnum,
  WorkModeEnum,
} from 'lib/generated/graphql';
import { SelectOption } from 'components/molecules/jobs/filters/asyncSelect';

export type JobFiltersType = {
  companyLocation: CompanyLocationEnum[];
  focuses?: Focus[];
  city?: SelectOption | null;
  maxSalary?: number;
  minSalary?: number;
  order: OrderEnum;
  workMode?: WorkModeEnum[];
  seniority?: SeniorityEnum[];
  techs?: SelectOption[] | null;
  type?: string[];
  page?: number;
};

export const DEFAULT_VALUE: JobFiltersType = {
  companyLocation: [],
  order: OrderEnum.Newer,
  page: 1,
  focuses: [],
  seniority: [],
  workMode: [],
  type: [],
};

const jobsShowcaseFiltersStore = createStore(
  { name: 'jobs_showcase_filters' },
  withProps<JobFiltersType>(DEFAULT_VALUE)
);

persistState(jobsShowcaseFiltersStore, {
  key: 'jobs_showcase_filters',
  storage: sessionStorageStrategy,
});

export const $filters = jobsShowcaseFiltersStore.pipe(select((state) => state));

export const setFilters = (filters: JobFiltersType) =>
  jobsShowcaseFiltersStore.update(() => filters);

export const setCompanyLocation = (
  companyLocation: JobFiltersType['companyLocation']
) =>
  jobsShowcaseFiltersStore.update((state) => ({
    ...state,
    companyLocation,
    page: DEFAULT_VALUE.page,
  }));

export const setFocuses = (focuses: JobFiltersType['focuses']) =>
  jobsShowcaseFiltersStore.update((state) => ({
    ...state,
    focuses,
    page: DEFAULT_VALUE.page,
  }));

export const setCity = (city: JobFiltersType['city']) =>
  jobsShowcaseFiltersStore.update((state) => ({
    ...state,
    city,
    page: DEFAULT_VALUE.page,
  }));

export const setMaxSalary = (maxSalary: JobFiltersType['maxSalary']) =>
  jobsShowcaseFiltersStore.update((state) => ({
    ...state,
    maxSalary,
    page: DEFAULT_VALUE.page,
  }));

export const setMinSalary = (minSalary: JobFiltersType['minSalary']) =>
  jobsShowcaseFiltersStore.update((state) => ({
    ...state,
    minSalary,
    page: DEFAULT_VALUE.page,
  }));

export const setOrder = (order: JobFiltersType['order']) =>
  jobsShowcaseFiltersStore.update((state) => ({
    ...state,
    order,
    page: DEFAULT_VALUE.page,
  }));

export const setWorkMode = (workMode: JobFiltersType['workMode']) =>
  jobsShowcaseFiltersStore.update((state) => ({
    ...state,
    workMode,
    page: DEFAULT_VALUE.page,
  }));

export const setSeniority = (seniority: JobFiltersType['seniority']) =>
  jobsShowcaseFiltersStore.update((state) => ({
    ...state,
    seniority,
    page: DEFAULT_VALUE.page,
  }));

export const setTechs = (techs: JobFiltersType['techs']) =>
  jobsShowcaseFiltersStore.update((state) => ({
    ...state,
    techs,
    page: DEFAULT_VALUE.page,
  }));

export const setType = (type: JobFiltersType['type']) =>
  jobsShowcaseFiltersStore.update((state) => ({
    ...state,
    type,
    page: DEFAULT_VALUE.page,
  }));

export const setPage = (page: JobFiltersType['page']) =>
  jobsShowcaseFiltersStore.update((state) => ({
    ...state,
    page,
  }));

export function useJobFilters() {
  const [filters] = useObservable($filters);

  return {
    filters,
    setCompanyLocation,
    setFocuses,
    setCity,
    setMaxSalary,
    setMinSalary,
    setOrder,
    setWorkMode,
    setSeniority,
    setTechs,
    setType,
    setPage,
    setFilters,
  };
}
